import { inject, Injectable } from '@angular/core';
import { firstValueFrom, Observable, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { FeatureService } from './feature.service';
import { LayerManagementService } from './layer-management.service';
import { MapLayer } from './layers';
import { SearchResultEntityType } from './models/advanced-search.model';

@Injectable({ providedIn: 'root' })
export class SearchResultLayerCheckService {
  private readonly layerManagement = inject(LayerManagementService);
  private readonly featureService = inject(FeatureService);

  /**
   * Static required display-layer names per entity type, according to user feedback:
   *
   * Address, Lot/plan, Parcel: Use PARCEL layer.
   *
   * Coordinates: no need to check layers as results are displayed as a point on the map.
   *
   * Property: Use PROPERTY + PARCEL layers.
   *
   * Crown description: PARCEL + the crown-specific annotation layers.
   *
   *
   * surveyMark:  Includes both WFS-resolved layers. when WFS query returns no results,
   *              fallback by loading from SMES preset in LayerManagementService
   *
   * surveyLabel: Resolved dynamically by identifier via WFS:
   *              - pfi-based: LTO/VMAP_LV_CPO -> SURVEY_LABEL, RETIRED_SURVEY_LABEL, PLAN
   *              - label_id-based: ROADS_CORP_SP_WITHOUT_FN, ROADS_CORP_SP_WITH_FN,
   *                ROADS_CORP_CONTROL_PLANS
   *
   * Quick search civic/geographic types:
   * - lga:       LGA boundaries
   * - locality:  locality boundaries
   * - road:      road names only
   * - parish / township: parish-township layer
   * - place:     VICNAMES place/FOI layers, some of them don't have ID for WFS query
   */
  private static readonly STATIC_REQUIRED: Partial<
    Record<SearchResultEntityType, string[]>
  > = {
    address: ['PARCEL'],
    lot: ['PARCEL'],
    parcel: ['PARCEL'],
    property: ['PROPERTY', 'PARCEL'],
    crownDescription: ['PARCEL', 'CROWN_ALLOTMENT', 'CROWN_LAND'],
    lga: ['LGA'],
    locality: ['LOCALITIES_BOUNDARIES'],
    road: ['ROAD_NAMES', 'PLACE_NAMES'],
    parish: ['PARISH_TOWNSHIP'],
    township: ['PARISH_TOWNSHIP'],
    place: ['PLACE_NAMES'],
  };

  /**
   * Returns the specific display layer names required for the search results.
   */
  getRequiredLayerNames(
    type: SearchResultEntityType,
    identifiers: string[] = [],
  ): Observable<string[]> {
    switch (type) {
      case 'coordinates':
        return of([]);

      case 'surveyMark':
        if (!identifiers.length) return of([]);
        return this.featureService
          .getSurveyMarkLayersForMarkIds(identifiers.map(Number))
          .pipe(
            switchMap((layers) => {
              // If WFS query returned layers, use them; otherwise fall back to SMES preset
              if (layers.size > 0) return of([...layers]);
              return this.layerManagement.getPresetLayerNames('SMES');
            }),
          );

      case 'surveyLabel': {
        const staticSurveyLabelLayers =
          SearchResultLayerCheckService.STATIC_REQUIRED['surveyLabel'] ?? [];
        if (!identifiers.length) return of(staticSurveyLabelLayers);
        return this.featureService
          .getSurveyLabelLayersForIdentifiers(identifiers)
          .pipe(
            switchMap((layers) => {
              // If WFS query returned layers, use them; otherwise fall back to SURVEY_LABEL
              const allLayers = new Set(layers);
              staticSurveyLabelLayers.forEach((l) => allLayers.add(l));
              if (allLayers.size > 0) return of([...allLayers]);
              return of(['SURVEY_LABEL']);
            }),
          );
      }

      case 'address':
      case 'lot':
      case 'parcel':
      case 'property':
      case 'crownDescription':
      case 'lga':
      case 'locality':
      case 'road':
      case 'parish':
      case 'township':
      case 'place':
        return of(SearchResultLayerCheckService.STATIC_REQUIRED[type] ?? []);
    }
  }

  /**
   * Filters required layer names to those currently not active (absent or status=false).
   * Returns a reactive Observable so it updates when the user changes layers.
   */
  getMissingLayers(requiredLayerNames: string[]): Observable<string[]> {
    if (!requiredLayerNames.length) return of([]);
    return this.layerManagement.activeLayers$.pipe(
      map((active) => {
        const on = new Set(active.filter((l) => l.status).map((l) => l.name));
        return requiredLayerNames.filter((n) => !on.has(n));
      }),
    );
  }

  /**
   * Enables all named layers.
   * - If a layer is not in activeLayers at all → addLayers (inserts with correct zIndex)
   * - If a layer is in activeLayers but status=false → toggleLayer(name, true)
   *
   * Logs which layers were successfully enabled and warns about missing layers.
   */
  async enableLayers(layerNames: string[]): Promise<void> {
    if (!layerNames.length) return;

    const [layersMap, current] = await Promise.all([
      firstValueFrom(this.layerManagement.lassiLayersMap$),
      firstValueFrom(this.layerManagement.activeLayers$),
    ]);

    const inList = new Set(current.map((l) => l.name));
    const toAdd = layerNames
      .filter((n) => !inList.has(n))
      .map((n) => layersMap.get(n))
      .filter((l): l is MapLayer => l !== undefined);

    const toToggle = layerNames.filter((n) => inList.has(n));

    const notFound = layerNames.filter(
      (n) => !toAdd.map((l) => l.name).includes(n) && !toToggle.includes(n),
    );

    if (notFound.length > 0) {
      console.warn(
        'SearchResultLayerCheck: Some layers not found in layer system:',
        notFound,
        'Available layers:',
        Array.from(layersMap.keys()),
      );
    }

    if (toAdd.length) await this.layerManagement.addLayers(toAdd);
    for (const name of toToggle)
      await this.layerManagement.toggleLayer(name, true);
  }
}
